import { create } from 'zustand';
import { User, LoginRequest, RegisterRequest } from '@/types';
import { apiClient, setToken, removeToken, getToken } from '@/services/api';
import toast from 'react-hot-toast';
import { errorLog } from '@/utils/logger';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  login: (data: LoginRequest) => Promise<boolean>;
  register: (data: RegisterRequest) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>((set, _get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (data: LoginRequest) => {
    try {
      const response = await apiClient.auth.login(data);
      
      if (response.success && response.token && response.user) {
        setToken(response.token);
        localStorage.setItem('heperum-user', JSON.stringify(response.user));
        set({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
        });
        
        toast.success(`Welcome back, ${response.user.fullName}!`);
        return true;
      } else {
        toast.error(response.message || 'Login failed');
        return false;
      }
    } catch (error: any) {
      errorLog('Login error:', error);
      toast.error(error.message || 'Login failed');
      return false;
    }
  },

  register: async (data: RegisterRequest) => {
    try {
      const response = await apiClient.auth.register(data);
      
      if (response.success && response.token && response.user) {
        setToken(response.token);
        localStorage.setItem('heperum-user', JSON.stringify(response.user));
        set({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
        });
        
        toast.success(`Welcome, ${response.user.fullName}! Account created successfully.`);
        return true;
      } else {
        toast.error(response.message || 'Registration failed');
        return false;
      }
    } catch (error: any) {
      errorLog('Registration error:', error);
      toast.error(error.message || 'Registration failed');
      return false;
    }
  },

  logout: () => {
    removeToken();
    localStorage.removeItem('heperum-user');
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
    toast.success('Logged out successfully');
  },

  checkAuth: async () => {
    const token = getToken();
    
    if (!token) {
      set({ 
        user: null,
        isAuthenticated: false, 
        isLoading: false 
      });
      return;
    }

    // Immediately restore user from localStorage if available
    // This prevents logout during page refresh
    let hasLocalUser = false;
    try {
      const storedUser = localStorage.getItem('heperum-user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
        hasLocalUser = true;
      }
    } catch (parseError) {
      // Silently handle parse errors - not critical
    }

    try {
      const response = await apiClient.auth.verify(token);
      
      if (response.success && response.user) {
        // Update with fresh user data from server
        localStorage.setItem('heperum-user', JSON.stringify(response.user));
        set({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        // Token is invalid, remove it and logout
        removeToken();
        localStorage.removeItem('heperum-user');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error: any) {
      // Check if it's an authentication error (401/403)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        // Token is definitely invalid, logout
        removeToken();
        localStorage.removeItem('heperum-user');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      } else if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK' || !error?.response) {
        // Network error - maintain session if we already restored from localStorage
        if (hasLocalUser) {
          // Keep the user authenticated from localStorage
          set(state => ({ ...state, isLoading: false }));
        } else {
          // No local user data, mark as unauthenticated
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      } else {
        // Other errors - treat as authentication failure
        removeToken();
        localStorage.removeItem('heperum-user');
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    }
  },

  updateUser: (user: User) => {
    localStorage.setItem('heperum-user', JSON.stringify(user));
    set({ user });
  },
}));